import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import apiClient from "@/api/client";
import type {
  ChatLinkPreview,
  ChatMessage,
  ChatMessageEditRecord,
  ChatRoomAttachment,
  ChatRoomAttachmentsPage,
  ChatRoomBrief,
  ChatRoomDetail,
} from "../types";

const ROOMS_KEY = ["chat", "rooms"] as const;
const BADGE_KEY = ["chat", "unread-summary"] as const;

function messagesKey(roomId: string) {
  return ["chat", "rooms", roomId, "messages"] as const;
}

function roomKey(roomId: string) {
  return ["chat", "rooms", roomId] as const;
}

interface ChatRoomsOptions {
  archivedOnly?: boolean;
  enabled?: boolean;
}

export function useChatRooms(opts: ChatRoomsOptions = {}) {
  const { archivedOnly = false, enabled = true } = opts;
  return useQuery({
    queryKey: [...ROOMS_KEY, { archivedOnly }],
    enabled,
    queryFn: async () => {
      const params = archivedOnly
        ? "?include_archived=true&archived_only=true"
        : "";
      const r = await apiClient.get<{ data: ChatRoomBrief[] }>(
        `/chat/rooms/${params}`,
      );
      return r.data.data ?? [];
    },
    staleTime: 30_000,
    // Realtime is the primary update path: every chat:{room} event and every
    // chat-user:{id} membership event invalidates the list. The interval is
    // only a safety net for a dead WebSocket, so keep it rare — this endpoint
    // is the most expensive list in the app to serve.
    refetchInterval: 300_000,
    refetchOnWindowFocus: refetchIfInvalidated,
  });
}

export function useChatRoom(roomId: string | null) {
  return useQuery({
    queryKey: roomId ? roomKey(roomId) : ["chat", "rooms", "none"],
    enabled: !!roomId,
    queryFn: async () => {
      const r = await apiClient.get<{ data: ChatRoomDetail }>(
        `/chat/rooms/${roomId}/`,
      );
      return r.data.data;
    },
  });
}

interface MessagesPage {
  data: ChatMessage[];
  meta: {
    has_more: boolean;
    next_cursor: string | null;
    has_newer?: boolean;
    prev_cursor?: string | null;
    count: number;
  };
}

/**
 * Infinite cursor pagination — last 50 by default; `fetchNextPage` walks back
 * in time (older). We expose all messages flattened, ordered oldest → newest.
 */
export function useChatMessagesInfinite(roomId: string | null) {
  return useInfiniteQuery({
    queryKey: roomId ? messagesKey(roomId) : ["chat", "messages", "none"],
    enabled: !!roomId,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if (pageParam) params.set("before", pageParam);
      const r = await apiClient.get<MessagesPage>(
        `/chat/rooms/${roomId}/messages/?${params}`,
      );
      return r.data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.meta.has_more ? lastPage.meta.next_cursor : undefined,
    // Polling fallback: if the WebSocket subscription fails to deliver a
    // message, this ensures it appears within 2 min. When WebSocket works
    // normally messages arrive via useChatRoomLive cache patches, so a short
    // interval here would only re-serialize the same page server-side.
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
  });
}

type AroundPageParam =
  | { around: string }
  | { before: string }
  | { after: string };

/**
 * Anchored history view for jump-to-old-message: the initial page is centered
 * on `anchorId` (?around=), then fetchNextPage walks OLDER (?before=) and
 * fetchPreviousPage walks NEWER (?after=). Pages flatten the same way as the
 * live buffer: [...pages].reverse().flatMap → oldest → newest.
 */
export function useChatMessagesAround(
  roomId: string | null,
  anchorId: string | null,
) {
  return useInfiniteQuery({
    queryKey: [
      "chat",
      "rooms",
      roomId ?? "none",
      "messages-around",
      anchorId ?? "none",
    ],
    enabled: !!roomId && !!anchorId,
    initialPageParam: { around: anchorId ?? "" } as AroundPageParam,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: "50" });
      if ("around" in pageParam) params.set("around", pageParam.around);
      else if ("before" in pageParam) params.set("before", pageParam.before);
      else params.set("after", pageParam.after);
      const r = await apiClient.get<MessagesPage>(
        `/chat/rooms/${roomId}/messages/?${params}`,
      );
      return r.data;
    },
    getNextPageParam: (lastPage): AroundPageParam | undefined =>
      lastPage.meta.has_more && lastPage.meta.next_cursor
        ? { before: lastPage.meta.next_cursor }
        : undefined,
    getPreviousPageParam: (firstPage): AroundPageParam | undefined =>
      firstPage.meta.has_newer && firstPage.meta.prev_cursor
        ? { after: firstPage.meta.prev_cursor }
        : undefined,
    // Anchored history is a read-only snapshot — no polling; live updates
    // keep flowing into the main messages query and show up after the user
    // returns to the newest view.
  });
}

interface CreateRoomBody {
  kind: "direct" | "group";
  name: string;
  member_ids: string[];
}

export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateRoomBody) => {
      const r = await apiClient.post<{ data: ChatRoomDetail }>(
        "/chat/rooms/",
        body,
      );
      return r.data.data;
    },
    onSuccess: () => {
      invalidateChatRoomLists(qc);
    },
  });
}

interface UpdateRoomVars {
  roomId: string;
  name?: string;
  pictureManagedFileId?: string | null;
}

export function useUpdateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: UpdateRoomVars) => {
      const payload: Record<string, unknown> = {};
      if (vars.name !== undefined) payload.name = vars.name;
      if (vars.pictureManagedFileId !== undefined) {
        payload.picture_managed_file = vars.pictureManagedFileId;
      }
      const r = await apiClient.patch<{ data: ChatRoomDetail }>(
        `/chat/rooms/${vars.roomId}/`,
        payload,
      );
      return r.data.data;
    },
    onSuccess: (data, vars) => {
      invalidateChatRoomLists(qc);
      qc.setQueryData(roomKey(vars.roomId), data);
    },
  });
}

interface SendMessageVars {
  body: string;
  attachmentIds?: string[];
  replyToId?: string | null;
  linkPreviews?: ChatLinkPreview[];
  mentionIds?: string[];
}

export function useSendMessage(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SendMessageVars) => {
      const payload: Record<string, unknown> = { body: vars.body };
      if (vars.attachmentIds && vars.attachmentIds.length > 0) {
        payload.attachment_managed_files = vars.attachmentIds;
      }
      if (vars.replyToId) {
        payload.reply_to = vars.replyToId;
      }
      if (vars.linkPreviews && vars.linkPreviews.length > 0) {
        payload.link_previews = vars.linkPreviews;
      }
      if (vars.mentionIds && vars.mentionIds.length > 0) {
        payload.mention_ids = vars.mentionIds;
      }
      const r = await apiClient.post<{ data: ChatMessage }>(
        `/chat/rooms/${roomId}/messages/`,
        payload,
      );
      return r.data.data;
    },
    onSuccess: (sentMessage) => {
      // Always invalidate messages for this room so the sent message is
      // visible even when the WebSocket is unavailable (production fallback).
      qc.invalidateQueries({ queryKey: messagesKey(roomId) });
      // Invalidate room-list queries for unread count / last message preview,
      // but only those — not all queries under the broad ROOMS_KEY prefix.
      invalidateChatRoomLists(qc);
      // Optimistically inject the sent message while the refetch is in-flight
      // so there's no visible delay even on slow connections.
      qc.setQueryData<{
        pages: { data: ChatMessage[]; meta: unknown }[];
        pageParams: unknown[];
      }>(messagesKey(roomId), (old) => {
        if (!old) return old;
        const exists = old.pages.some((p) =>
          p.data.some((m) => m.id === sentMessage.id),
        );
        if (exists) return old;
        const pages = old.pages.slice();
        pages[0] = {
          ...pages[0],
          data: [...pages[0].data, sentMessage],
        };
        return { ...old, pages };
      });
    },
  });
}

export async function fetchLinkPreview(
  url: string,
): Promise<ChatLinkPreview | null> {
  try {
    const r = await apiClient.get<{ data: ChatLinkPreview }>(
      `/chat/link-preview/?url=${encodeURIComponent(url)}`,
    );
    return r.data.data ?? null;
  } catch {
    return null;
  }
}

export async function postTypingPing(roomId: string): Promise<void> {
  try {
    await apiClient.post(`/chat/rooms/${roomId}/typing/`);
  } catch {
    // Typing pings are best-effort.
  }
}

export function useMessageSearch(roomId: string, q: string) {
  return useQuery({
    queryKey: ["chat", "rooms", roomId, "search", q],
    enabled: !!roomId && q.trim().length >= 2,
    queryFn: async () => {
      const r = await apiClient.get<{
        data: ChatMessage[];
        meta: { count: number; query: string };
      }>(
        `/chat/rooms/${roomId}/messages/search/?q=${encodeURIComponent(q.trim())}&limit=500`,
      );
      return r.data.data ?? [];
    },
    staleTime: 5_000,
  });
}

/**
 * Global content search — GET /chat/rooms/search/?q= matches message bodies
 * across every room the viewer belongs to; each hit carries `room`.
 */
export function useGlobalMessageSearch(q: string) {
  return useQuery({
    queryKey: ["chat", "global-search", q.trim()],
    enabled: q.trim().length >= 2,
    queryFn: async () => {
      const r = await apiClient.get<{
        data: ChatMessage[];
        meta: { count: number; query: string };
      }>(`/chat/rooms/search/?q=${encodeURIComponent(q.trim())}`);
      return r.data.data ?? [];
    },
    staleTime: 5_000,
  });
}

/**
 * Galeria „Multimedia i pliki" rozmowy — całą historię załączników serwer oddaje
 * stronami (60 sztuk), więc panel dociąga kolejne przy przewijaniu zamiast
 * ładować kilkaset miniatur naraz. `counts` przychodzi zawsze dla OBU zakładek,
 * żeby nagłówek pokazywał liczby bez drugiego zapytania.
 */
export function useRoomAttachments(
  roomId: string,
  kind: "media" | "files",
  enabled: boolean,
) {
  const PAGE = 60;
  return useInfiniteQuery({
    queryKey: ["chat", "rooms", roomId, "attachments", kind],
    enabled: enabled && !!roomId,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const r = await apiClient.get<{
        data: ChatRoomAttachment[];
        meta: {
          counts: { media: number; files: number; all: number };
          has_more: boolean;
        };
      }>(
        `/chat/rooms/${roomId}/attachments/?kind=${kind}&limit=${PAGE}&offset=${pageParam}`,
      );
      return {
        items: r.data.data ?? [],
        counts: r.data.meta?.counts ?? { media: 0, files: 0, all: 0 },
        hasMore: !!r.data.meta?.has_more,
        offset: pageParam as number,
      };
    },
    getNextPageParam: (last) =>
      last.hasMore ? (last.offset as number) + PAGE : undefined,
    staleTime: 30_000,
  });
}

/** Spłaszczenie stron do jednej listy + liczniki z ostatniej odpowiedzi. */
export function flattenAttachmentPages(
  pages: { items: ChatRoomAttachment[]; counts: ChatRoomAttachmentsPage["counts"]; hasMore: boolean }[] | undefined,
): ChatRoomAttachmentsPage {
  const list = pages ?? [];
  return {
    items: list.flatMap((p) => p.items),
    counts: list[list.length - 1]?.counts ?? { media: 0, files: 0, all: 0 },
    hasMore: !!list[list.length - 1]?.hasMore,
  };
}

export function useEditMessage(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: string }) => {
      const r = await apiClient.patch<{ data: ChatMessage }>(
        `/chat/rooms/${roomId}/messages/${vars.id}/`,
        { body: vars.body },
      );
      return r.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesKey(roomId) });
    },
  });
}

export function useDeleteMessage(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/chat/rooms/${roomId}/messages/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: messagesKey(roomId) });
    },
  });
}

export function useToggleReaction(roomId: string) {
  return useMutation({
    mutationFn: async (vars: { messageId: string; emoji: string }) => {
      const r = await apiClient.post<{ data: ChatMessage }>(
        `/chat/rooms/${roomId}/messages/${vars.messageId}/reactions/`,
        { emoji: vars.emoji },
      );
      return r.data.data;
    },
  });
}

export function useClearReaction(roomId: string) {
  return useMutation({
    mutationFn: async (vars: { messageId: string }) => {
      const r = await apiClient.delete<{ data: ChatMessage }>(
        `/chat/rooms/${roomId}/messages/${vars.messageId}/reactions/`,
      );
      return r.data.data;
    },
  });
}

export function useEditHistory(roomId: string, messageId: string | null) {
  return useQuery({
    queryKey: ["chat", "rooms", roomId, "messages", messageId, "edit-history"],
    enabled: !!messageId,
    queryFn: async () => {
      const r = await apiClient.get<{ data: ChatMessageEditRecord[] }>(
        `/chat/rooms/${roomId}/messages/${messageId}/edit-history/`,
      );
      return r.data.data ?? [];
    },
  });
}

export function useMarkRead(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ChatRoomDetail | null> => {
      // Hand over this device's push endpoint: the backend tells the user's
      // OTHER devices to drop the room's notification, and this one is
      // already taken care of by dismissRoomNotification below.
      let endpoint: string | undefined;
      try {
        const { getExistingSubscription } = await import("./webPush");
        endpoint = (await getExistingSubscription())?.endpoint;
      } catch {
        // No push subscription here — nothing to exclude.
      }
      const r = await apiClient.post<{ data: ChatRoomDetail }>(
        `/chat/rooms/${roomId}/read/`,
        endpoint ? { endpoint } : {},
      );
      return r.data.data ?? null;
    },
    onSuccess: (room) => {
      // This mutation runs on every message that lands in an open room, so it
      // must not fan out into refetches. ``ROOMS_KEY`` is a PREFIX of the room
      // detail and message-page keys, so invalidating it here re-fetched
      // /chat/rooms/<id>/ AND every loaded message page on each mark-read —
      // a busy room burned the whole 300/min user bucket and the detail
      // endpoint started answering 429 ("Żądanie zostało zdławione").
      // The endpoint already answers with the fresh room detail, so seed the
      // cache from the response instead of asking for it again.
      if (room) qc.setQueryData(roomKey(roomId), room);
      invalidateChatRoomLists(qc);
      // Dismiss the OS push notification only when the window is actually
      // in focus — if it's in the background the notification must stay
      // visible until the user activates the window.
      if (document.hasFocus()) {
        import("./webPush").then(({ dismissRoomNotification }) =>
          dismissRoomNotification(roomId),
        );
      }
    },
  });
}

export function useArchiveRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roomId: string) => {
      await apiClient.post(`/chat/rooms/${roomId}/archive/`);
    },
    onSuccess: () => {
      invalidateChatRoomLists(qc);
    },
  });
}

export function useAddRoomMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    // `grantChatAccess` leci tylko z okna członków i tylko po potwierdzeniu przez CEO:
    // backend nadaje wtedy kontu podwykonawcy wstęp do GG. Bez tej flagi dodanie firmy
    // bez dostępu odbija się błędem — nadanie dostępu nie ma się dziać przy okazji.
    mutationFn: async (v: string | { userId: string; grantChatAccess?: boolean }) => {
      const { userId, grantChatAccess } = typeof v === "string" ? { userId: v, grantChatAccess: false } : v;
      const r = await apiClient.post<{ data: ChatRoomDetail }>(
        `/chat/rooms/${roomId}/members/`,
        grantChatAccess ? { user_id: userId, grant_chat_access: true } : { user_id: userId },
      );
      return r.data.data;
    },
    onSuccess: (data) => {
      invalidateChatRoomLists(qc);
      qc.setQueryData(roomKey(roomId), data);
    },
  });
}

export function useRemoveRoomMember(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const r = await apiClient.delete<{ data: ChatRoomDetail }>(
        `/chat/rooms/${roomId}/members/${userId}/`,
      );
      return r.data.data;
    },
    onSuccess: (data) => {
      invalidateChatRoomLists(qc);
      qc.setQueryData(roomKey(roomId), data);
    },
  });
}

export function useUnarchiveRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roomId: string) => {
      await apiClient.post(`/chat/rooms/${roomId}/unarchive/`);
    },
    onSuccess: () => {
      invalidateChatRoomLists(qc);
    },
  });
}

function stampDesc(nullsFirst: boolean) {
  // ISO strings from the API may or may not carry microseconds, so compare
  // parsed instants rather than text.
  return (a: string | null, b: string | null): number => {
    if (a === b) return 0;
    if (a === null) return nullsFirst ? -1 : 1;
    if (b === null) return nullsFirst ? 1 : -1;
    return Date.parse(b) - Date.parse(a);
  };
}

const pinnedAtDesc = stampDesc(false);
// Postgres puts NULLs first under DESC, so a room without any message yet
// leads its block — mirrored here so an optimistic patch lands where the
// server's next response will.
const activityDesc = stampDesc(true);

/**
 * Backend order of the room list (ChatRoomViewSet.get_queryset), mirrored for
 * optimistic cache patches: pinned rooms first, most recently pinned on top,
 * then everything else by newest activity.
 */
export function sortRoomsForList(rooms: ChatRoomBrief[]): ChatRoomBrief[] {
  return [...rooms].sort(
    (a, b) =>
      pinnedAtDesc(a.pinned_at, b.pinned_at) ||
      activityDesc(a.last_message_at, b.last_message_at) ||
      activityDesc(a.created_at, b.created_at),
  );
}

interface SetRoomPinnedVars {
  roomId: string;
  pinned: boolean;
}

/**
 * Pin / unpin a room to the top of MY list (WhatsApp-style, per user). The
 * row moves immediately — the list is the most expensive query in the app,
 * so the refetch that confirms the server's timestamp should never be what
 * the click waits for.
 */
export function useSetRoomPinned() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ roomId, pinned }: SetRoomPinnedVars) => {
      const r = await apiClient.post<{ data: ChatRoomDetail }>(
        `/chat/rooms/${roomId}/${pinned ? "pin" : "unpin"}/`,
      );
      return r.data.data;
    },
    onMutate: ({ roomId, pinned }) => {
      // A provisional "now" keeps the just-pinned room at the head of the
      // pinned block, which is where the server puts it as well.
      const pinnedAt = pinned ? new Date().toISOString() : null;
      const patch = <T extends ChatRoomBrief>(room: T): T =>
        room.id === roomId
          ? { ...room, pinned_at: pinnedAt, is_pinned: pinned }
          : room;
      qc.setQueriesData<ChatRoomBrief[]>(
        { predicate: (query) => isRoomListKey(query.queryKey) },
        (old) => (old ? sortRoomsForList(old.map(patch)) : old),
      );
      qc.setQueryData<ChatRoomDetail>(roomKey(roomId), (old) =>
        old ? patch(old) : old,
      );
    },
    onSuccess: (data, { roomId }) => {
      qc.setQueryData(roomKey(roomId), data);
    },
    onSettled: () => {
      invalidateChatRoomLists(qc);
    },
  });
}

/**
 * GG badge in the top bar — mounted on EVERY page of the app. It used to share
 * the room-list query and sum unread_count client-side, which meant every page
 * view anywhere pulled the full room list (members, avatars, last-message
 * previews) to render two numbers. It now asks for exactly those two numbers.
 */
export function useChatBadgeCounts(enabled: boolean) {
  return useQuery({
    queryKey: BADGE_KEY,
    enabled,
    queryFn: async () => {
      const r = await apiClient.get<{
        data: { rooms: number; messages: number };
      }>("/chat/rooms/unread-summary/");
      return r.data.data ?? { rooms: 0, messages: 0 };
    },
    staleTime: 30_000,
    // Realtime invalidation (useChatGlobalLive) is the primary update path; the
    // interval is a WebSocket-down fallback only.
    refetchInterval: 300_000,
    refetchOnWindowFocus: refetchIfInvalidated,
  });
}

export const chatQueryKeys = {
  rooms: ROOMS_KEY,
  badge: BADGE_KEY,
  messages: messagesKey,
  room: roomKey,
};

/**
 * Refresh room-LIST queries (unread counts, last-message preview, ordering,
 * sidebar badge) without touching per-room queries. List keys are exactly
 * ["chat", "rooms", { archivedOnly }] — the same prefix as room detail
 * (["chat","rooms",<id>]) and messages (["chat","rooms",<id>,"messages"])
 * queries, so a broad ROOMS_KEY invalidation would needlessly refetch those.
 */
export function invalidateChatRoomLists(qc: QueryClient): void {
  // ONE debounced pass for every source. A single message in an open room used to
  // trigger two or three: the room channel's own debounce, the chat-user nudge's
  // debounce and the mark-read — each refetching the room list(s) and the badge.
  if (listRefreshTimer) clearTimeout(listRefreshTimer);
  listRefreshTimer = setTimeout(() => {
    listRefreshTimer = null;
    void qc.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key[0] !== "chat") return false;
        // The top-bar badge reads its own two-number endpoint, so it has to be
        // refreshed here too — it is the one room-list consumer that is mounted
        // even when no chat screen is open.
        if (key[1] === BADGE_KEY[1]) return true;
        return isRoomListKey(key);
      },
      // A hidden tab only marks them stale — it gets nudged on every message of
      // every room just like the visible one, and nobody is looking at it. The
      // lists reload when the tab comes back (refetchIfInvalidated).
      refetchType: document.visibilityState === "hidden" ? "none" : "active",
    });
  }, LIST_REFRESH_DEBOUNCE_MS);
}

/** Room-list refreshes arriving within this window collapse into one pass. */
const LIST_REFRESH_DEBOUNCE_MS = 800;
let listRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Focus refetch only for what an invalidation marked stale while the tab was hidden
 *  (the app keeps refetchOnWindowFocus off globally). */
function refetchIfInvalidated(query: { state: { isInvalidated: boolean } }): boolean {
  return query.state.isInvalidated;
}

/** Exactly the room-LIST keys: ["chat", "rooms", { archivedOnly }]. */
function isRoomListKey(key: readonly unknown[]): boolean {
  return (
    key[0] === "chat" &&
    key[1] === "rooms" &&
    key.length === 3 &&
    typeof key[2] === "object"
  );
}
